import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { marginFields, SCHEME_COLOR_OPTIONS, visibilityField } from '../_shared/blockFields.js';
import { marginStyle, num, schemeColor, str, visibilityCss } from '../_shared/blockRender.js';
/**
 * Shrine `divider` block — config-by-config. A pure-CSS horizontal separator: a single coloured bar
 * whose `height`, `width` (% of container), `color`, corner radius and alignment within the row are all
 * theme-configurable, plus the standard Margins + Visibility groups. No JS — Shrine renders an inert
 * `<div>`.
 *
 * Shrine's markup wraps the bar in a flex row (`--image-alignment` controls justify) and exposes
 * `Full_mobile_width` to force a 100%-width edge-to-edge bar on mobile. We mirror all of it. We KEEP
 * Shrine's exact ids/labels/defaults/order (`color`, `height`, `width`, `Full_mobile_width`,
 * `alignment`, `border_radius`, `margin_*`) and additionally offer a `colorScheme` select — when set to
 * anything other than "custom" it resolves to a storefront scheme var and overrides the raw `color`
 * (so the divider can track the active colour scheme, matching the rest of our blocks).
 */
export const dividerBlockSchema = {
    type: 'divider',
    name: 'Divider',
    fields: [
        visibilityField,
        // Shrine `color` (default #E1E1E1). Used unless `colorScheme` selects a scheme var.
        { id: 'color', type: 'color', label: 'Color', default: { ref: 'custom', value: '#E1E1E1' } },
        {
            id: 'colorScheme',
            type: 'select',
            label: 'Color scheme',
            info: 'Use a scheme colour instead of the custom colour above.',
            default: 'custom',
            options: [{ value: 'custom', label: 'Custom (use Color)' }, ...SCHEME_COLOR_OPTIONS],
        },
        {
            id: 'height',
            type: 'range',
            label: 'Height',
            min: 1,
            max: 10,
            step: 1,
            default: 1,
            unit: 'px',
        },
        {
            id: 'width',
            type: 'range',
            label: 'Width',
            min: 0,
            max: 100,
            step: 10,
            default: 100,
            unit: '%',
        },
        {
            id: 'fullMobileWidth',
            type: 'checkbox',
            label: 'Full container width on mobile',
            info: 'The divider will be edge-to-edge on mobile if Width is set to 100%.',
            default: false,
        },
        {
            id: 'alignment',
            aiConfigurable: true,
            type: 'select',
            label: 'Alignment',
            default: 'center',
            options: [
                { value: 'flex-start', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'flex-end', label: 'Right' },
            ],
        },
        {
            id: 'borderRadius',
            type: 'range',
            label: 'Corner radius',
            info: 'Only visible with bigger heights.',
            min: 0,
            max: 5,
            step: 1,
            default: 0,
            unit: 'px',
        },
        ...marginFields(15, 15),
    ],
};
/** Read a Shrine `color` setting (ColorValue | raw string) defensively, falling back to Shrine's default. */
function readColor(v, fallback) {
    if (typeof v === 'string')
        return v;
    if (v && typeof v === 'object') {
        const c = v;
        if (c.ref === 'custom' && typeof c.value === 'string')
            return c.value;
        if (c.ref === 'theme' && typeof c.token === 'string')
            return `var(--aqx-theme-${c.token})`;
    }
    return fallback;
}
/**
 * Horizontal divider — pure CSS, SSR-safe. The bar sits in a flex row whose `justify-content` follows
 * `alignment`; `Full_mobile_width` forces the bar to 100% width below the 750px Shrine breakpoint via
 * BOTH `@media` (live storefront) and `@container aqx-page` (builder device-preview), matching how our
 * other responsive blocks reflow.
 */
const DividerBlock = ({ block }) => {
    const bs = block.settings;
    const cls = `aqx-dv-${block.id}`;
    const scheme = str(bs.colorScheme, 'custom');
    const color = scheme !== 'custom' ? schemeColor(scheme) : readColor(bs.color, '#E1E1E1');
    const height = num(bs.height, 1);
    const width = num(bs.width, 100);
    const radius = num(bs.borderRadius, 0);
    const align = str(bs.alignment, 'center');
    const fullMobile = bs.fullMobileWidth === true;
    const rowStyle = {
        display: 'flex',
        justifyContent: align,
        ...marginStyle(bs, 15, 15),
    };
    const barStyle = {
        height: `${height}px`,
        width: `${width}%`,
        background: color,
        borderRadius: `${radius}px`,
        display: 'block',
    };
    // `Full_mobile_width` → force the bar to 100% width on mobile (<750px). Emitted in BOTH @media and
    // @container so the builder device-preview reflows too. No-op when the toggle is off.
    const mobileRule = fullMobile ? `.${cls}__bar{width:100%}` : '';
    const css = (mobileRule
        ? `@media(max-width:749px){${mobileRule}}@container aqx-page (max-width:749px){${mobileRule}}`
        : '') + visibilityCss(cls, bs.visibility);
    return (_jsxs("div", { className: cls, style: rowStyle, children: [css ? _jsx("style", { dangerouslySetInnerHTML: { __html: css } }) : null, _jsx("div", { className: `${cls}__bar`, style: barStyle, "aria-hidden": "true" })] }));
};
export const dividerBlock = {
    type: 'divider',
    schema: dividerBlockSchema,
    Component: DividerBlock,
    meta: { label: 'Divider' },
};
//# sourceMappingURL=index.js.map